/* eslint-disable max-lines */

import { Component, Input, inject } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { ModalOptions } from '@ionic/core';
import { firstValueFrom } from 'rxjs';

import { BaseFormComponent, DatasetFormData } from 'src/app/@shared/form/base-form.component';
import { C } from 'src/app/@shared/constants';
import { Cluster } from '@api/cluster/@shared/cluster.entity';
import { ClusterAddressMemberType, ClusterMemberType, ClusterModel } from 'src/app/cluster/@shared/cluster.model';
import { ClusterService } from 'src/app/cluster/@shared/cluster.service';
import { FilterModalMeta } from 'src/app/filter/filter.page';
import { FormFieldModel } from 'src/app/@shared/form/form-field/form-field.model';
import { FormFieldValue } from 'src/app/@shared/form/form.types';
import { Organization } from '@api/organization/@shared/organization.entity';
import {
  OrganizationFilterResultForWhere,
  getOrganizationFilterResultByWhereFilter,
  getOrganizationWhereFilterByFilterResult,
  normalizeOrganizationFilterResult,
} from 'src/app/@shared/organization-filter.adapter';
import { OrganizationListPage } from 'src/app/organization/organization-list/organization-list.page';
import { OrganizationModel } from 'src/app/organization/@shared/organization.model';
import { Person } from '@api/person/@shared/person.entity';
import {
  PersonFilterResultForWhere,
  getPersonFilterResultByWhereFilter,
  getPersonWhereFilterByFilterResult,
} from 'src/app/@shared/person-filter.adapter';
import { PersonListPage } from 'src/app/person/person-list/person-list.page';
import { PersonModel } from 'src/app/person/@shared/person.model';
import { Topic } from '@api/topic/@shared/topic.entity';
import { TopicListPage } from 'src/app/topic/topic-list/topic-list.page';
import { TopicModel } from 'src/app/topic/@shared/topic.model';
import { TopicService } from 'src/app/topic/@shared/topic.service';
import { WhereFilter } from '@common/filter.decorator';
import { getConfigs } from 'src/app/@shared/form/form-helper';
import { getTopicFilterData, getTopicWhereFilter } from 'src/app/cluster/@shared/cluster-topic-filter';

import { ContactPersonModel } from '../contact-person.model';

type SelectModalId = 'selfHelpAddresses' | 'otherAddresses' | 'persons' | 'contactPersons' | 'topics';
type SelectModalItem = OrganizationModel | PersonModel | ContactPersonModel | TopicModel;

const IS_SMART_CLUSTER_FIELD_ID = 'isSmartCluster';
const INCLUDE_SELF_HELP_ADDRESSES_FIELD_ID = 'includeSelfHelpAddresses';
const INCLUDE_OTHER_ADDRESSES_FIELD_ID = 'includeOtherAddresses';
const INCLUDE_PERSONS_FIELD_ID = 'includePersons';
const INCLUDE_CONTACT_PERSONS_FIELD_ID = 'includeContactPersons';
const INCLUDE_TOPICS_FIELD_ID = 'includeTopics';
const INCLUDE_TYPE_BY_FIELD_ID: Partial<Record<string, ClusterMemberType>> = {
  [INCLUDE_CONTACT_PERSONS_FIELD_ID]: 'contactPerson',
  [INCLUDE_OTHER_ADDRESSES_FIELD_ID]: 'otherAddress',
  [INCLUDE_PERSONS_FIELD_ID]: 'person',
  [INCLUDE_SELF_HELP_ADDRESSES_FIELD_ID]: 'selfHelpAddress',
  [INCLUDE_TOPICS_FIELD_ID]: 'topic',
};
const SELECT_MODAL_PREVIEW_LIMIT = C.DEFAULT_LIMIT;
const SELECT_MODAL_TO_MEMBER_TYPE: Record<SelectModalId, ClusterMemberType> = {
  contactPersons: 'contactPerson',
  otherAddresses: 'otherAddress',
  persons: 'person',
  selfHelpAddresses: 'selfHelpAddress',
  topics: 'topic',
};

@Component({
  selector: 'cluster-form',
  standalone: false,
  styleUrls: ['./cluster-form.component.scss'],
  templateUrl: './cluster-form.component.html',
})
export class ClusterFormComponent extends BaseFormComponent<ClusterModel> {
  @Input() public isEditMode: boolean;
  public organizationTopicLabelById: Record<string, string> = {};
  public selfHelpAddressFilterFormData: OrganizationFilterResultForWhere = {};
  public otherAddressFilterFormData: OrganizationFilterResultForWhere = {};
  public contactPersonFilterFormData: PersonFilterResultForWhere = {};
  public personFilterFormData: PersonFilterResultForWhere = {};
  public topicFilterFormData: any = {};
  public showFilterForm = false;

  public selectedItems: { [key in SelectModalId]: SelectModalItem[] } = {
    contactPersons: [],
    otherAddresses: [],
    persons: [],
    selfHelpAddresses: [],
    topics: [],
  };

  public badgePreviewItems: { [key in SelectModalId]: SelectModalItem[] } = {
    contactPersons: [],
    otherAddresses: [],
    persons: [],
    selfHelpAddresses: [],
    topics: [],
  };

  public hasBadgePreviewOverflow: Record<SelectModalId, boolean> = {
    contactPersons: false,
    otherAddresses: false,
    persons: false,
    selfHelpAddresses: false,
    topics: false,
  };

  public isMultiSelect: { [key in SelectModalId]: boolean } = {
    contactPersons: true,
    otherAddresses: true,
    persons: true,
    selfHelpAddresses: true,
    topics: true,
  };

  public includeByType: Required<Pick<NonNullable<ClusterModel['includeByType']>, ClusterMemberType>> = {
    contactPerson: false,
    otherAddress: false,
    person: false,
    selfHelpAddress: false,
    topic: false,
  };

  protected clusterService = inject(ClusterService);

  private allSelectionByType: ClusterModel['allSelectionByType'] = {};
  private smartWhereFilterByType: ClusterModel['whereFilterByType'] = {};

  private modalController = inject(ModalController);
  private topicService = inject(TopicService);

  public onValueUpdated(currentValue: FormFieldValue | FormFieldValue[], currentFieldId: string): void {
    this.datasetData[currentFieldId] = currentValue;

    const memberType = INCLUDE_TYPE_BY_FIELD_ID[currentFieldId];

    if (memberType) {
      this.onIncludeByTypeChanged(memberType, !!currentValue);

      return;
    }

    if (currentFieldId !== IS_SMART_CLUSTER_FIELD_ID) {
      return;
    }

    // currentValue is a boolean in that case
    this.showFilterForm = currentValue as boolean;

    this.prepareForm();
    this.patchFormByDataset(this.datasetData);
    this.editForm.markAsDirty();
  }

  public async openModal(result: { field: FormFieldModel; formGroupIndex?: number }): Promise<void> {
    const { field } = result;

    let modal: HTMLIonModalElement | null;

    switch (field.id) {
      case 'selfHelpAddresses':
        modal = await this.getOrganizationModal('selfHelpAddress',
          this.selectedItems[field.id as SelectModalId] as OrganizationModel[],
          this.isMultiSelect.selfHelpAddresses);
        break;

      case 'otherAddresses':
        modal = await this.getOrganizationModal('otherAddress',
          this.selectedItems[field.id as SelectModalId] as OrganizationModel[],
          this.isMultiSelect.otherAddresses);
        break;

      case 'persons':
        modal = await this.getPersonModal('person',
          this.selectedItems[field.id as SelectModalId] as PersonModel[],
          this.isMultiSelect.persons);
        break;

      case 'contactPersons':
        modal = await this.getPersonModal('contactPerson', this.selectedItems.contactPersons as PersonModel[], true);
        break;

      case 'topics':
        modal = await this.getTopicModal(this.selectedItems.topics as TopicModel[]);
        break;

      default:
        throw new Error(`Case for ${field.id} not handled`);
    }

    if (!modal) {
      throw new Error('Modal has not been created');
    }

    await modal.present();

    const { data } = await modal.onWillDismiss();

    if (!data) {
      return;
    }

    this.onModalValuesReceived(field, data);
  }

  public onOrganizationFilterChanged(type: ClusterAddressMemberType, filterData: OrganizationFilterResultForWhere & FilterModalMeta): void {
    if (!this.includeByType[type]) {
      return;
    }

    const { selectedTopics = [] } = filterData;

    for (const topic of selectedTopics) {
      if (topic?.id && topic.title) {
        this.organizationTopicLabelById[topic.id] = topic.title;
      }
    }

    const normalizedFilterData = normalizeOrganizationFilterResult(filterData);
    const whereFilter = getOrganizationWhereFilterByFilterResult(normalizedFilterData);

    if (type === 'selfHelpAddress') {
      this.selfHelpAddressFilterFormData = {
        ...filterData as OrganizationFilterResultForWhere,
        ...normalizedFilterData as OrganizationFilterResultForWhere,
      };
    } else {
      this.otherAddressFilterFormData = {
        ...filterData as OrganizationFilterResultForWhere,
        ...normalizedFilterData as OrganizationFilterResultForWhere,
      };
    }

    if (Object.keys(whereFilter).length) {
      this.smartWhereFilterByType[type] = whereFilter as WhereFilter<Organization>;
    } else {
      delete this.smartWhereFilterByType[type];
    }

    this.editForm.markAsDirty();
  }

  public onSelfHelpAddressFilterChanged(filterData: OrganizationFilterResultForWhere & FilterModalMeta): void {
    this.onOrganizationFilterChanged('selfHelpAddress', filterData);
  }

  public onOtherAddressFilterChanged(filterData: OrganizationFilterResultForWhere & FilterModalMeta): void {
    this.onOrganizationFilterChanged('otherAddress', filterData);
  }

  public onPersonFilterChanged(filterData: PersonFilterResultForWhere): void {
    if (!this.includeByType.person) {
      return;
    }

    const whereFilter = getPersonWhereFilterByFilterResult(filterData);

    this.personFilterFormData = {
      ...filterData as PersonFilterResultForWhere,
    };

    if (Object.keys(whereFilter).length) {
      this.smartWhereFilterByType.person = whereFilter as WhereFilter<Person>;
    } else {
      delete this.smartWhereFilterByType.person;
    }

    this.editForm.markAsDirty();
  }

  public onContactPersonFilterChanged(filterData: PersonFilterResultForWhere): void {
    if (!this.includeByType.contactPerson) {
      return;
    }
    this.contactPersonFilterFormData = {
      ...filterData,
    };
    const where = getPersonWhereFilterByFilterResult(filterData);

    if (Object.keys(where).length) {
      this.smartWhereFilterByType.contactPerson = where;
    } else {
      delete this.smartWhereFilterByType.contactPerson;
    }
    this.editForm.markAsDirty();
  }

  public onTopicFilterChanged(filterData: any): void {
    if (!this.includeByType.topic) {
      return;
    }
    this.topicFilterFormData = filterData;
    const where = getTopicWhereFilter(filterData);

    if (Object.keys(where).length) {
      this.smartWhereFilterByType.topic = where;
    } else {
      delete this.smartWhereFilterByType.topic;
    }
    this.editForm.markAsDirty();
  }

  public removeItemFromSelectModal(key: SelectModalId, value: string): void {
    const memberType = this.getMemberTypeBySelectModalKey(key);

    this.selectedItems[key] = this.selectedItems[key].filter((item) => {
      return item.id !== value;
    });

    if (this.allSelectionByType?.[memberType]?.isAllSelected) {
      const excludedIds = new Set(this.allSelectionByType[memberType]?.excludedIds || []);

      excludedIds.add(value);
      this.allSelectionByType[memberType] = {
        ...this.allSelectionByType[memberType] || {},
        excludedIds: Array.from(excludedIds),
        isAllSelected: true,
      } as any;
    }

    this.updateBadgePreviewState(key);
    this.updateSelectionAvailability();

    this.editForm.patchValue({
      [key]: this.selectedItems[key].length
        ? this.selectedItems[key]
        : null,
    });

    this.editForm.markAsDirty();
  }

  protected onDataReceived(data: ClusterModel): void {
    this.datasetData = {
      ...data,
    };
    (this.datasetData as any)[INCLUDE_SELF_HELP_ADDRESSES_FIELD_ID] = data.includeByType?.selfHelpAddress === true;
    (this.datasetData as any)[INCLUDE_OTHER_ADDRESSES_FIELD_ID] = data.includeByType?.otherAddress === true;
    (this.datasetData as any)[INCLUDE_PERSONS_FIELD_ID] = data.includeByType?.person === true;
    (this.datasetData as any)[INCLUDE_CONTACT_PERSONS_FIELD_ID] = data.includeByType?.contactPerson === true;
    (this.datasetData as any)[INCLUDE_TOPICS_FIELD_ID] = data.includeByType?.topic === true;

    this.showFilterForm = this.datasetData.isSmartCluster;
    this.includeByType = {
      contactPerson: data.includeByType?.contactPerson === true,
      otherAddress: this.datasetData.includeByType?.otherAddress === true,
      person: this.datasetData.includeByType?.person === true,
      selfHelpAddress: data.includeByType?.selfHelpAddress === true,
      topic: data.includeByType?.topic === true,
    };

    this.prepareForm();
    this.patchFormByDataset(this.datasetData);
  }

  protected prepareForm(): void {
    this.formId = this.getFormId();

    const { formConfig, fieldsetConfig } = getConfigs(this.formId);
    const normalizedFormConfig = this.showFilterForm
      ? Object.fromEntries(Object.entries(formConfig).filter(([key]) => {
        return ![
          'selfHelpAddresses',
          'otherAddresses',
          'persons',
          'contactPersons',
          'organizations',
          'topics',
        ].includes(key);
      }))
      : Object.fromEntries(Object.entries(formConfig).filter(([key]) => {
        return key !== INCLUDE_SELF_HELP_ADDRESSES_FIELD_ID && key !== INCLUDE_OTHER_ADDRESSES_FIELD_ID
          && key !== INCLUDE_PERSONS_FIELD_ID && key !== INCLUDE_CONTACT_PERSONS_FIELD_ID && key !== INCLUDE_TOPICS_FIELD_ID;
      }));

    if (!normalizedFormConfig || !Object.keys(normalizedFormConfig).length) {
      console.error('formConfig missing', this.formId);
    } else {
      this.setConfig(normalizedFormConfig as any, fieldsetConfig);
    }
  }

  protected onFormPatchedByDataset(): void {
    // patchFormByDataset() does not handle this, so we do it here
    if (this.showFilterForm) {
      this.patchSmartClusterFormState();
    } else {
      this.patchDumbClusterFormState();
    }
    this.updateSelectionAvailability();

    // we don't allow switch smart cluster mode in edit mode
    if (this.isEditMode) {
      const isSmartClusterControl = this.editForm.get(IS_SMART_CLUSTER_FIELD_ID);

      if (isSmartClusterControl) {
        isSmartClusterControl.disable();
      }
    }
  }

  private patchSmartClusterFormState(): void {
    const whereFilterByType = this.datasetData.whereFilterByType || {};
    const includeByType = this.datasetData.includeByType || {};

    this.smartWhereFilterByType = {
      contactPerson: whereFilterByType.contactPerson,
      otherAddress: whereFilterByType.otherAddress as WhereFilter<Organization>,
      person: whereFilterByType.person as WhereFilter<Person>,
      selfHelpAddress: whereFilterByType.selfHelpAddress as WhereFilter<Organization>,
      topic: whereFilterByType.topic as WhereFilter<Topic>,
    };
    this.includeByType = {
      contactPerson: includeByType.contactPerson === true,
      otherAddress: includeByType.otherAddress === true,
      person: includeByType.person === true,
      selfHelpAddress: includeByType.selfHelpAddress === true,
      topic: includeByType.topic === true,
    };
    this.editForm.patchValue({
      [INCLUDE_CONTACT_PERSONS_FIELD_ID]: this.includeByType.contactPerson,
      [INCLUDE_OTHER_ADDRESSES_FIELD_ID]: this.includeByType.otherAddress,
      [INCLUDE_PERSONS_FIELD_ID]: this.includeByType.person,
      [INCLUDE_SELF_HELP_ADDRESSES_FIELD_ID]: this.includeByType.selfHelpAddress,
      [INCLUDE_TOPICS_FIELD_ID]: this.includeByType.topic,
    }, {
      emitEvent: false,
    });
    this.selfHelpAddressFilterFormData = getOrganizationFilterResultByWhereFilter(this.smartWhereFilterByType.selfHelpAddress);
    this.otherAddressFilterFormData = getOrganizationFilterResultByWhereFilter(this.smartWhereFilterByType.otherAddress);
    this.personFilterFormData = getPersonFilterResultByWhereFilter(this.smartWhereFilterByType.person);
    this.contactPersonFilterFormData = getPersonFilterResultByWhereFilter(this.smartWhereFilterByType.contactPerson);
    this.topicFilterFormData = getTopicFilterData(this.smartWhereFilterByType.topic);
    this.hydrateOrganizationTopicLabels().catch((error) => {
      console.error('[ClusterFormComponent] Failed to hydrate topic labels for smart-cluster filter', error);
    });
  }

  private patchDumbClusterFormState(): void {
    this.selectedItems.selfHelpAddresses = (this.datasetData.organizations || []).filter((organization) => {
      return organization.type === 'selfHelpAddress';
    });
    this.selectedItems.otherAddresses = (this.datasetData.organizations || []).filter((organization) => {
      return organization.type === 'otherAddress';
    });
    this.selectedItems.persons = this.datasetData.persons || [];
    this.selectedItems.contactPersons = this.datasetData.contactPersons || [];
    this.selectedItems.topics = this.datasetData.topics || [];

    this.editForm.patchValue({
      contactPersons: this.selectedItems.contactPersons.length
        ? this.selectedItems.contactPersons
        : null,
      otherAddresses: this.selectedItems.otherAddresses.length
        ? this.selectedItems.otherAddresses
        : null,
      persons: this.selectedItems.persons.length
        ? this.selectedItems.persons
        : null,
      selfHelpAddresses: this.selectedItems.selfHelpAddresses.length
        ? this.selectedItems.selfHelpAddresses
        : null,
      topics: this.selectedItems.topics.length
        ? this.selectedItems.topics
        : null,
    }, {
      emitEvent: false,
    });

    this.allSelectionByType = this.datasetData.allSelectionByType || {};
    this.updateBadgePreviewState('selfHelpAddresses');
    this.updateBadgePreviewState('otherAddresses');
    this.updateBadgePreviewState('persons');
    this.updateBadgePreviewState('contactPersons');
    this.updateBadgePreviewState('topics');
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  protected async handleRequest(formData: DatasetFormData): Promise<boolean> {
    let dataToBeSaved: Partial<Cluster>;

    // we only store the filter for smart clusters
    if (this.showFilterForm) {
      dataToBeSaved = {
        includeByType: this.includeByType,
        isSmartCluster: true,
        isTopicCluster: this.includeByType.topic,
        notes: formData.notes,
        title: formData.title,
        whereFilterByType: this.smartWhereFilterByType as any,
      };
    } else {
      // Replace formdata here with selectedValues
      // TODO: Check wether this is still strictly necessary as we currently
      this.insertSelectedItems(formData);
      dataToBeSaved = {
        allSelectionByType: this.allSelectionByType as any,
        contactPersons: formData.contactPersons,
        isSmartCluster: false,
        isTopicCluster: this.selectedItems.topics.length > 0 || !!this.allSelectionByType?.topic?.isAllSelected,
        notes: formData.notes,
        organizations: formData.organizations,
        persons: formData.persons,
        title: formData.title,
        topics: formData.topics,
      };
    }

    if (this.isEditMode) {
      return !!await this.clusterService.patch(this.datasetData.id, dataToBeSaved);
    }

    return !!await this.clusterService.create(dataToBeSaved, 'clusters');
  }

  private insertSelectedItems(data: DatasetFormData): void {
    const organizationItems = [...this.selectedItems.selfHelpAddresses, ...this.selectedItems.otherAddresses] as OrganizationModel[];

    data.organizations = this.getEntityRefs(organizationItems);
    data.persons = this.getEntityRefs(this.selectedItems.persons as PersonModel[]);
    data.contactPersons = this.getEntityRefs(this.selectedItems.contactPersons as ContactPersonModel[]);
    data.topics = this.getEntityRefs(this.selectedItems.topics as TopicModel[]);
  }

  private getEntityRefs(items: SelectModalItem[]): { id: string }[] {
    return items.map((item) => {
      return {
        id: item.id,
      };
    });
  }

  private onIncludeByTypeChanged(type: ClusterMemberType, isIncluded: boolean): void {
    if (type === 'topic' && isIncluded) {
      this.setTopicOnlyMembership();

      return;
    }
    if (type !== 'topic' && isIncluded && this.includeByType.topic) {
      this.includeByType.topic = false;
      this.topicFilterFormData = {};
      delete this.smartWhereFilterByType.topic;
      this.editForm.patchValue({
        [INCLUDE_TOPICS_FIELD_ID]: false,
      }, {
        emitEvent: false,
      });
    }
    this.includeByType = {
      ...this.includeByType,
      [type]: isIncluded,
    };

    if (!isIncluded) {
      this.clearMemberFilter(type);
    }

    this.editForm.markAsDirty();
  }

  private setTopicOnlyMembership(): void {
    this.includeByType = {
      contactPerson: false,
      otherAddress: false,
      person: false,
      selfHelpAddress: false,
      topic: true,
    };
    delete this.smartWhereFilterByType.otherAddress;
    delete this.smartWhereFilterByType.person;
    delete this.smartWhereFilterByType.contactPerson;
    delete this.smartWhereFilterByType.selfHelpAddress;
    this.editForm.patchValue({
      [INCLUDE_CONTACT_PERSONS_FIELD_ID]: false,
      [INCLUDE_OTHER_ADDRESSES_FIELD_ID]: false,
      [INCLUDE_PERSONS_FIELD_ID]: false,
      [INCLUDE_SELF_HELP_ADDRESSES_FIELD_ID]: false,
    }, {
      emitEvent: false,
    });
    this.editForm.markAsDirty();
  }

  private clearMemberFilter(type: ClusterMemberType): void {
    if (type === 'selfHelpAddress') {
      this.selfHelpAddressFilterFormData = {};
    }

    if (type === 'otherAddress') {
      this.otherAddressFilterFormData = {};
    }

    if (type === 'contactPerson') { this.contactPersonFilterFormData = {}; }

    if (type === 'person') {
      this.personFilterFormData = {};
    }

    if (type === 'topic') {
      this.topicFilterFormData = {};
    }

    delete this.smartWhereFilterByType[type];
  }

  private async getOrganizationModal(type: string, selectedItems: OrganizationModel[], isMultiSelect: boolean = false): Promise<HTMLIonModalElement> {
    const memberType = type as ClusterAddressMemberType;
    const componentProps = {
      enableAllSelection: true,
      formType: type,
      isModal: true,
      isMultiSelect,
      selectedAllState: this.allSelectionByType?.[memberType] || null,
      selectedItems,
    };

    const options: ModalOptions = {
      component: OrganizationListPage,
      componentProps,
      cssClass: 'proto-modal proto-modal--list',
      mode: 'md',
    };

    const topModal = await this.modalController.getTop();

    options.presentingElement = topModal || document.querySelector('ion-router-outlet');

    const modal = await this.modalController.create(options);

    options.componentProps.presentingElement = modal;

    return modal;
  }

  private async getPersonModal(type: string, selectedItems: PersonModel[], isMultiSelect: boolean = false): Promise<HTMLIonModalElement> {
    const componentProps = {
      contactPersons: type === 'contactPerson',
      enableAllSelection: true,
      formType: 'person',
      isModal: true,
      isMultiSelect,
      selectedAllState: this.allSelectionByType?.[type === 'contactPerson'
        ? 'contactPerson'
        : 'person'] || null,
      selectedItems: [...selectedItems],
    };

    const options: ModalOptions = {
      component: PersonListPage,
      componentProps,
      cssClass: 'proto-modal proto-modal--list',
      mode: 'md',
    };

    const topModal = await this.modalController.getTop();

    options.presentingElement = topModal || document.querySelector('ion-router-outlet');

    const modal = await this.modalController.create(options);

    options.componentProps.presentingElement = modal;

    return modal;
  }

  private async getTopicModal(selectedItems: TopicModel[]): Promise<HTMLIonModalElement> {
    const options: ModalOptions = {
      component: TopicListPage,
      componentProps: {
        enableAllSelection: true,
        isModal: true,
        isMultiSelect: true,
        selectedAllState: this.allSelectionByType?.topic || null,
        selectedItems: [...selectedItems],
      },
      cssClass: 'proto-modal proto-modal--list',
      mode: 'md',
    };
    const topModal = await this.modalController.getTop();

    options.presentingElement = topModal || document.querySelector('ion-router-outlet');

    return this.modalController.create(options);
  }

  private onModalValuesReceived(field: FormFieldModel, value: any): void {
    this.editForm.markAsDirty();

    const values = Array.isArray(value)
      ? value
      : value?.items || [];
    const selection = Array.isArray(value)
      ? null
      : value?.selection;

    this.selectedItems[field.id as SelectModalId] = values;

    if (field.id === 'selfHelpAddresses' || field.id === 'otherAddresses') {
      const memberType = SELECT_MODAL_TO_MEMBER_TYPE[field.id];

      if (selection) {
        this.allSelectionByType[memberType] = selection;
      } else {
        delete this.allSelectionByType[memberType];
      }
    } else if (field.id === 'persons' || field.id === 'contactPersons' || field.id === 'topics') {
      const memberType = SELECT_MODAL_TO_MEMBER_TYPE[field.id];

      if (selection) {
        this.allSelectionByType[memberType] = selection;
      } else {
        delete this.allSelectionByType[memberType];
      }
    }

    this.editForm.patchValue({
      [field.id]: values,
    });
    this.updateBadgePreviewState(field.id as SelectModalId);
    this.updateSelectionAvailability();
  }

  private updateSelectionAvailability(): void {
    if (this.showFilterForm) {
      return;
    }
    const hasTopics = !!this.selectedItems.topics?.length || !!this.allSelectionByType?.topic?.isAllSelected;
    const contactTypes: SelectModalId[] = ['selfHelpAddresses', 'otherAddresses', 'persons', 'contactPersons'];
    const hasContacts = contactTypes.some((key) => {
      return !!this.selectedItems[key]?.length || !!this.allSelectionByType?.[SELECT_MODAL_TO_MEMBER_TYPE[key]]?.isAllSelected;
    });
    const setDisabled = (key: string, disabled: boolean): void => {
      const control = this.editForm?.get(key);

      if (disabled) {
        control?.disable({
          emitEvent: false,
        });
      } else {
        control?.enable({
          emitEvent: false,
        });
      }
    };

    for (const key of contactTypes) {
      setDisabled(key, hasTopics);
    }
    setDisabled('topics', hasContacts);
    setDisabled(IS_SMART_CLUSTER_FIELD_ID, hasTopics || this.isEditMode);
  }

  private getMemberTypeBySelectModalKey(key: SelectModalId): ClusterMemberType {
    return SELECT_MODAL_TO_MEMBER_TYPE[key];
  }

  private updateBadgePreviewState(type: SelectModalId): void {
    const memberType = SELECT_MODAL_TO_MEMBER_TYPE[type];
    const hasAllSelectionState = !!this.allSelectionByType?.[memberType]?.isAllSelected;
    const sortedItems = this.selectedItems[type]
      .slice()
      .sort((left, right) => {
        return `${left.displayName || ''}`.localeCompare(`${right.displayName || ''}`);
      });

    this.hasBadgePreviewOverflow[type] = hasAllSelectionState || sortedItems.length >= SELECT_MODAL_PREVIEW_LIMIT;
    this.badgePreviewItems[type] = sortedItems.slice(0, SELECT_MODAL_PREVIEW_LIMIT);
  }

  private async hydrateOrganizationTopicLabels(): Promise<void> {
    const selectedTopicIds = (this.selfHelpAddressFilterFormData.topics || [])
      .concat(this.otherAddressFilterFormData.topics || [])
      .filter((id): id is string => {
        return !!id;
      });
    const topicIds = Array.from(new Set(selectedTopicIds));

    if (!topicIds.length) {
      return;
    }

    const storeId = this.getTopicLabelStoreId();
    const forceReload = this.shouldForceReloadTopicLabels(storeId, topicIds);
    const topics = await firstValueFrom(this.topicService.getContent(storeId, {
      filter: {
        take: topicIds.length,
        where: {
          id: {
            in: topicIds,
          },
        },
      } as any,
      forceReload,
      type: 'list',
    }));

    for (const topic of topics || []) {
      this.organizationTopicLabelById[topic.id] = topic.title;
    }

    this.refreshOrganizationFilterFormData();
  }

  private getTopicLabelStoreId(): string {
    return `cluster-form-topic-labels:${this.datasetData?.id || 'new-cluster'}`;
  }

  private shouldForceReloadTopicLabels(storeId: string, topicIds: string[]): boolean {
    const expectedIds = topicIds.slice().sort();
    const appliedFilter = this.topicService.getMetaData(storeId)?.appliedFilter as any;
    const cachedIds = Array.isArray(appliedFilter?.where?.id?.in)
      ? [...appliedFilter.where.id.in].sort()
      : [];

    if (!cachedIds.length) {
      return false;
    }

    if (cachedIds.length !== expectedIds.length) {
      return true;
    }

    return expectedIds.some((id, index) => {
      return id !== cachedIds[index];
    });
  }

  private refreshOrganizationFilterFormData(): void {
    // Trigger ngOnChanges in inline filter component so existing selected ids are re-mapped to labels.
    this.selfHelpAddressFilterFormData = {
      ...this.selfHelpAddressFilterFormData,
    };
    this.otherAddressFilterFormData = {
      ...this.otherAddressFilterFormData,
    };
  }
}
